type SimpleStyle = 'display' | 'position';

type ExtendedStyle =
  | 'overflow'
  | 'overflow-x'
  | 'overflow-y'
  | 'overscroll'
  | 'overscroll-x'
  | 'overscroll-y';

type LayoutStyle = SimpleStyle | ExtendedStyle;

type Display =
  | 'inline'
  | 'block'
  | 'inline-block'
  | 'flow-root'
  | 'flex'
  | 'inline-flex'
  | 'grid'
  | 'inline-grid'
  | 'contents'
  | 'table'
  | 'inline-table'
  | 'table-caption'
  | 'table-cell'
  | 'table-column'
  | 'table-column-group'
  | 'table-footer-group'
  | 'table-header-group'
  | 'table-row-group'
  | 'table-row'
  | 'list-item'
  | 'hidden'
  | 'sr-only'
  | 'not-sr-only';

type Overflow = 'auto' | 'hidden' | 'clip' | 'visible';

type Overscroll = 'auto' | 'contain' | 'none';

type Position = 'static' | 'relative' | 'absolute' | 'fixed' | 'sticky';

type Visibility = 'visible' | 'hidden' | 'collapse';

type SimpleValue = Display | Position | Visibility;

type ExtendedValue = Overflow | Overscroll;

type LayoutValue<T extends SimpleValue> = Record<T, T>;

type LayoutValueX<T extends ExtendedValue, U extends ExtendedStyle> = Record<
  T,
  `${U}-${T}`
>;

const display: Display[] = [
  'inline',
  'block',
  'inline-block',
  'flow-root',
  'flex',
  'inline-flex',
  'grid',
  'inline-grid',
  'contents',
  'table',
  'inline-table',
  'table-caption',
  'table-cell',
  'table-column',
  'table-column-group',
  'table-footer-group',
  'table-header-group',
  'table-row-group',
  'table-row',
  'list-item',
  'hidden',
  'sr-only',
  'not-sr-only',
];

const position: Position[] = [
  'static',
  'relative',
  'absolute',
  'fixed',
  'sticky',
];

const overflow: Overflow[] = ['auto', 'hidden', 'clip', 'visible'];

const overscroll: Overscroll[] = ['auto', 'contain', 'none'];

// Visibility is NOT an identity map: Tailwind's `hidden` is a DISPLAY utility
// (display:none), so `visibility='hidden'` must emit `invisible` to hide the
// element in place while it keeps its layout box (VWA-236). `visible` /
// `collapse` map to the like-named visibility utilities.
const visibilityClass = {
  visible: 'visible',
  hidden: 'invisible',
  collapse: 'collapse',
} satisfies Record<Visibility, string>;

type LayoutVariant<T extends LayoutStyle> = T extends 'display'
  ? LayoutValue<Display>
  : T extends 'overflow'
    ? LayoutValueX<Overflow, T>
    : T extends 'overflow-x'
      ? LayoutValueX<Overflow, T>
      : T extends 'overflow-y'
        ? LayoutValueX<Overflow, T>
        : T extends 'overscroll'
          ? LayoutValueX<Overscroll, T>
          : T extends 'overscroll-x'
            ? LayoutValueX<Overscroll, T>
            : T extends 'overscroll-y'
              ? LayoutValueX<Overscroll, T>
              : T extends 'position'
                ? LayoutValue<Position>
                : never;

const simple = {
  display,
  position,
} as const;

const extended = {
  overflow,
  'overflow-x': overflow,
  'overflow-y': overflow,
  overscroll,
  'overscroll-x': overscroll,
  'overscroll-y': overscroll,
} as const;

function generateSimpleVariants<T extends SimpleStyle>(
  value: T,
): LayoutVariant<T> {
  return Object.fromEntries(
    simple[value].map((curr) => [curr, curr]),
  ) as LayoutVariant<T>;
}

function generateExtendedVariants<T extends ExtendedStyle>(
  value: T,
): LayoutVariant<T> {
  return Object.fromEntries(
    extended[value].map((curr) => [curr, `${value}-${curr}`]),
  ) as LayoutVariant<T>;
}

const layoutVariants = {
  display: generateSimpleVariants('display'),
  position: generateSimpleVariants('position'),
  visibility: visibilityClass,
  overflow: generateExtendedVariants('overflow'),
  overflowX: generateExtendedVariants('overflow-x'),
  overflowY: generateExtendedVariants('overflow-y'),
  overscroll: generateExtendedVariants('overscroll'),
  overscrollX: generateExtendedVariants('overscroll-x'),
  overscrollY: generateExtendedVariants('overscroll-y'),
} as const;

type LayoutVariants = typeof layoutVariants;

export { type LayoutVariants, layoutVariants };
